/**
 * Decision logic for the action footer on agent tool-result cards.
 *
 * Tool UIs hand in (a) the entity ids the result references, (b) the handler
 * map of what they can actually do, and (c) the current chat-tool context.
 * `pickActions` returns which action is primary and which trail behind as
 * secondaries. The function is pure — context lookups belong in
 * `useChatToolContext`.
 *
 * Source of truth for the matrix is design.md (D2) in the
 * chat-tool-ui-improvements OpenSpec change.
 */

export type ToolActionKind =
  // Open a fresh exploration on the graph (no active view).
  | 'showOnGraph'
  // Open the result on the graph as a *new* exploration, exiting the active
  // viewpoint. Same handler as `showOnGraph`; rendered with a different
  // label so users understand they'll leave the current graph.
  | 'showSeparately'
  // Additive merge into the active viewpoint (saved investigation or
  // transient exploration). Surfaces as a removable chip.
  | 'addToGraph'
  // Paint the result on the current canvas without merging or navigating
  // away. Picked when every required entity is already loaded.
  | 'highlight'
  // Persist the current transient/active view as a saved investigation.
  | 'saveAsInvestigation'

export interface ChatToolContext {
  /** Where the chat is mounted. Affects post-action navigation only. */
  mode: 'fullscreen-agent' | 'sidebar'
  caseId: string
  /** Saved investigation id when one is active. */
  activeInvestigationId: string | null
  /** Whether a viewpoint exists (active saved OR transient preview). */
  hasActiveView: boolean
  /** Ids currently rendered on the canvas. Drives the highlight-vs-add
   * decision when an active view exists. */
  loadedEntityIds: ReadonlySet<string>
}

export interface PickActionsResult {
  primary: ToolActionKind | null
  secondary: ToolActionKind[]
}

/** Map of action kind → the handler the tool UI can execute for it. */
export type ToolActionHandlers = Partial<Record<ToolActionKind, () => void>>

/**
 * Choose primary + secondary actions for a tool result.
 *
 * @param requiredEntityIds ids the result needs on canvas to be meaningful.
 *   Used to decide highlight-vs-add.
 * @param available actions the tool UI is able to execute. Anything not in
 *   this set is dropped from the matrix.
 */
export function pickActions(
  requiredEntityIds: readonly string[],
  available: ReadonlySet<ToolActionKind>,
  ctx: ChatToolContext
): PickActionsResult {
  if (!ctx.hasActiveView) {
    return ordered(['showOnGraph'], available)
  }

  const allLoaded =
    requiredEntityIds.length > 0 && requiredEntityIds.every(id => ctx.loadedEntityIds.has(id))

  if (allLoaded) {
    // Highlight is the primary action; offer "Show separately" as a fallback
    // so the user can still reset the view to *only* this result if the
    // current graph is too busy to visually find the highlight.
    return ordered(['highlight', 'showSeparately'], available)
  }

  return ordered(['addToGraph', 'showSeparately'], available)
}

function ordered(
  preferred: readonly ToolActionKind[],
  available: ReadonlySet<ToolActionKind>
): PickActionsResult {
  const filtered = preferred.filter(k => available.has(k))
  return {
    primary: filtered[0] ?? null,
    secondary: filtered.slice(1),
  }
}
